import { Dictionary } from "../../util/collection/Dictionary";
import { KeyValuePair } from "../../util/collection/KeyValuePair";
import { Logger } from "../../util/log/Logger";
import { StringUtil } from "../../util/string/StringUtil";
/**
 * Locale.ts
 */
export class Locale {
    constructor(id, nativeMessage = null, breakWords = false, ignoreCase = false) {
        this.id = id;
        this.nativeMessage = nativeMessage;
        this.breakWords = breakWords;
        this.ignoreCase = ignoreCase;
        Locale.SUPPORTED.setValue(id, this);
    }
    static getLocale(id) {
        let locale = Locale.SUPPORTED.getValue(id);
        if (!locale) {
            Logger.warn("Locale " + id + " not found in supported locales.");
            locale = new Locale(id, id);
        }
        return locale;
    }
    is(locale) {
        return this == locale;
    }
    getId() {
        return this.id;
    }
    getNativeMessage() {
        return this.nativeMessage;
    }
    useNativeFont() {
        return this.nativeMessage != null;
    }
    isBreakWords() {
        return this.breakWords;
    }
    isIgnoreCase() {
        return this.ignoreCase;
    }
}
Locale.SUPPORTED = new Dictionary();
Locale.ENGLISH = new Locale("en-GB");
Locale.AMERICAN = new Locale("en-US");
Locale.GERMAN = new Locale("de-DE");
Locale.SWEDISH = new Locale("sv-SE");
Locale.SPANISH_LATAM = new Locale("es-419");
Locale.FINNISH = new Locale("fi-FI");
Locale.NORWEGIAN = new Locale("nn-NO");
Locale.PORTUGUESE = new Locale("pt-PT");
Locale.BRAZILIAN = new Locale("pt-BR");
Locale.DANISH = new Locale("da-DK");
Locale.MEXICAN = new Locale("es-MX");
Locale.SPANISH = new Locale("es-ES");
Locale.ESTONIAN = new Locale("et-EE");
Locale.FRENCH = new Locale("fr-FR");
Locale.INDONESIAN = new Locale("in-ID");
Locale.ITALIAN = new Locale("it-IT");
Locale.MALAYSIAN = new Locale("ms-MY");
Locale.DUTCH_BELGIUM = new Locale("nl-BE");
Locale.DUTCH = new Locale("nl-NL");
Locale.CZECH = new Locale("cs-CZ");
Locale.LITHUANIAN = new Locale("lt-LT");
Locale.ICELANDIC = new Locale("is-IS");
Locale.POLISH = new Locale("pl-PL");
Locale.ROMANIAN = new Locale("ro-RO");
Locale.TURKISH = new Locale("tr-TR");
Locale.HUNGARIAN = new Locale("hu-HU");
Locale.SLOVAKIAN = new Locale("sk-SK");
Locale.CROATIAN = new Locale("hr-HR");
Locale.FRENCH_CANADIAN = new Locale("fr-CA");
Locale.ENGLISH_CANADIAN = new Locale("en-CA");
Locale.ENGLISH_AFRICAN = new Locale("en-ZA");
Locale.ENGLISH_SWISS = new Locale("en-CH");
Locale.ARMENIAN = new Locale("hy-AM");
Locale.SERBIAN = new Locale("sr-SR");
/* Native Fonts */
Locale.GREEK = new Locale("gr-EL", "γραμμή", false, true);
Locale.VIETNAMESE = new Locale("vi-VN", "Lượt quay miễn phí", false, false);
Locale.THAI = new Locale("th-TH", "แถว", true, false);
Locale.CHINESE = new Locale("zh-CN", "条线", true, false);
Locale.JAPANESE = new Locale("ja-JP", "ライン", true, false);
Locale.RUSSIAN = new Locale("ru-RU", "линия", false, false);
Locale.BULGARIAN = new Locale("bg-BG", "Помощ", false, false);
Locale.KOREAN = new Locale("ko-KR", "라인", false, false);
Locale.GEORGIAN = new Locale("ka-GE", "ხაზი", false, true);
Locale.LATVIAN = new Locale("lv-LV", "laimēts", false, false);
/*
 * LanguageManager.ts
 */
export class LanguageManager {
    constructor() {
        if (LanguageManager.instance) {
            throw new Error("LanguageManager Singleton Already Initialised");
        }
        this.create();
        LanguageManager.instance = this;
    }
    //you can either override all keys or just one
    overrideMap(src, target, specificKey = null) {
        if (specificKey) {
            if (this.map.containsKey(specificKey)) {
                var value = this.map.getValue(specificKey);
                var modifiedString = value.replace(src, target);
                this.map.setValue(specificKey, modifiedString);
                console.log("LanguageManager changed " + src + " " + target + " on " + specificKey);
            }
            return;
        }
        for (var key in this.map) {
            if (this.map.containsKey(specificKey)) {
                var value = this.map.getValue(key);
                var modifiedString = value.replace(src, target);
                this.map.setValue(key, modifiedString);
            }
        }
    }
    create() {
        this.locale = Locale.ENGLISH;
        this.map = new Dictionary();
        this.missingLang = [];
    }
    add(key, value) {
        if (this.map.containsKey(key)) {
            Logger.warn("Language key already exists: " + key + " overriding with: " + value);
        }
        value = this.replaceParameters(value, [new KeyValuePair("{NL}", "\n")]);
        this.map.setValue(key, value);
    }
    hasLang(key) {
        return this.map.containsKey(key);
    }
    getLang(key, parameters) {
        let lang = key;
        if (this.hasLang(key)) {
            lang = this.map.getValue(key);
            if (parameters) {
                lang = this.replaceParameters(lang, parameters);
            }
        }
        else {
            if (this.missingLang.indexOf(key) < 0) {
                this.missingLang.push(key);
            }
        }
        return lang;
    }
    replaceParameters(lang, parameters) {
        for (let i = 0; i < parameters.length; i++) {
            let parameter = parameters[i];
            lang = StringUtil.replaceAll(lang, parameter.getKey(), parameter.getValue());
        }
        return lang;
    }
    setLocale(value) {
        this.locale = Locale.getLocale(value);
    }
    getLocale() {
        return this.locale;
    }
    isBreakWords() {
        return this.locale.isBreakWords();
    }
    getMissingLang() {
        return this.missingLang;
    }
    getUniqueChars(keys, toUpperCase = false) {
        let keysArray = keys ? keys : this.map.getKeys();
        let values = keysArray.map((key) => {
            return toUpperCase ? this.map.getValue(key).toUpperCase() : this.map.getValue(key);
        });
        return [...new Set(values.join(""))].join("");
    }
    static getInstance() {
        if (LanguageManager.instance === null) {
            LanguageManager.instance = new LanguageManager();
        }
        return LanguageManager.instance;
    }
}
LanguageManager.instance = null;
//# sourceMappingURL=LanguageManager.js.map